const STORE_NO_SLOT_LABEL = "No time slot";

export const DAILY_ORDERS_LABEL = "Daily orders";
export const STORE_ORDERS_LABEL = "Store orders";

const STORE_SLOT_KEY_PREFIX = "store-slot:";

export type DeliverySlotInfoLike = {
  id?: number;
  slot_name?: string;
  start_time?: string;
  end_time?: string;
};

function parseSlotInfo(raw: unknown): DeliverySlotInfoLike | null {
  if (!raw || typeof raw !== "object") return null;
  return raw as DeliverySlotInfoLike;
}

function formatTimeHm(time: string): string {
  const match = String(time).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return String(time).trim();
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

/** Human-readable label from API `delivery_slot_info` */
export function formatDeliverySlotLabel(
  info: DeliverySlotInfoLike | null | undefined
): string {
  if (!info) return "";
  const name =
    typeof info.slot_name === "string" ? info.slot_name.trim() : "";
  const start = info.start_time ? formatTimeHm(String(info.start_time)) : "";
  const end = info.end_time ? formatTimeHm(String(info.end_time)) : "";
  const range =
    start && end ? `${start} – ${end}` : start || end;
  if (name && range) return `${name} (${range})`;
  if (name) return name;
  if (range) return range;
  return "";
}

/** Subscription deliveries — no store time slot on API */
export function isDailySubscriptionOrder(
  order: Record<string, unknown> | null | undefined
): boolean {
  if (!order) return false;
  return String(order.order_type ?? "").toLowerCase() === "subscription";
}

/** Display / group label for store orders (uses delivery_slot_info, then legacy string) */
export function getOrderDeliverySlotLabel(
  order: Record<string, unknown> | null | undefined
): string {
  if (!order || isDailySubscriptionOrder(order)) return "";

  const fromInfo = formatDeliverySlotLabel(
    parseSlotInfo(order.delivery_slot_info)
  );
  if (fromInfo) return fromInfo;

  const raw = order.delivery_time_slot ?? order.delivery_time ?? "";
  return typeof raw === "string" ? raw.trim() : String(raw ?? "").trim();
}

export function getStoreOrderTimeSlot(
  order: Record<string, unknown> | null | undefined
): string {
  if (!order || isDailySubscriptionOrder(order)) return STORE_NO_SLOT_LABEL;
  const label = getOrderDeliverySlotLabel(order);
  return label.length > 0 ? label : STORE_NO_SLOT_LABEL;
}

/** @deprecated Prefer partitionDailyAndStoreOrders; not for card display */
export function getOrderDeliveryGroupKey(
  order: Record<string, unknown> | null | undefined
): string {
  if (isDailySubscriptionOrder(order)) return DAILY_ORDERS_LABEL;
  return getStoreOrderTimeSlot(order);
}

/** @deprecated Prefer getOrderDeliveryGroupKey */
export function getOrderTimeSlot(
  order: Record<string, unknown> | null | undefined
): string {
  return getOrderDeliveryGroupKey(order);
}

export function storeSlotExpandKey(slot: string): string {
  return `${STORE_SLOT_KEY_PREFIX}${slot}`;
}

export function isStoreSlotExpandKey(key: string): boolean {
  return key.startsWith(STORE_SLOT_KEY_PREFIX);
}

export function slotFromStoreExpandKey(key: string): string {
  return key.slice(STORE_SLOT_KEY_PREFIX.length);
}

function slotStartMinutes(slot: string): number {
  const match = slot.match(/(\d{1,2}):(\d{2})/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

export function sortTimeSlotKeys(slots: string[]): string[] {
  return [...slots].sort((a, b) => slotStartMinutes(a) - slotStartMinutes(b));
}

export function groupByTimeSlot<T>(
  items: T[],
  getSlot: (item: T) => string
): Record<string, T[]> {
  const groups: Record<string, T[]> = {};
  for (const item of items) {
    const slot = getSlot(item);
    if (!groups[slot]) groups[slot] = [];
    groups[slot].push(item);
  }
  return groups;
}

export function sortedTimeSlotEntries<T>(
  groups: Record<string, T[]>
): [string, T[]][] {
  return sortTimeSlotKeys(Object.keys(groups)).map((slot) => [slot, groups[slot]]);
}

export type DeliveryOrderPartition<T> = {
  dailyOrders: T[];
  storeSlotEntries: [string, T[]][];
};

/** Split subscription (daily) vs store (POS/online) and sort store buckets by slot time */
export function partitionDailyAndStoreOrders<T>(
  items: T[],
  toOrderRecord: (item: T) => Record<string, unknown>
): DeliveryOrderPartition<T> {
  const dailyOrders: T[] = [];
  const storeItems: T[] = [];

  for (const item of items) {
    if (isDailySubscriptionOrder(toOrderRecord(item))) {
      dailyOrders.push(item);
    } else {
      storeItems.push(item);
    }
  }

  const storeGroups = groupByTimeSlot(storeItems, (item) =>
    getStoreOrderTimeSlot(toOrderRecord(item))
  );

  return {
    dailyOrders,
    storeSlotEntries: sortedTimeSlotEntries(storeGroups),
  };
}

export function isDailyOrdersGroupKey(key: string): boolean {
  return key === DAILY_ORDERS_LABEL;
}

export function isStoreOrdersGroupKey(key: string): boolean {
  return key === STORE_ORDERS_LABEL;
}

/** Keys to expand by default for basket / today's lists */
export function defaultExpandedGroupKeys(
  partition: DeliveryOrderPartition<unknown>
): string[] {
  const keys: string[] = [];
  if (partition.dailyOrders.length > 0) {
    keys.push(DAILY_ORDERS_LABEL);
  }
  if (partition.storeSlotEntries.length > 0) {
    keys.push(STORE_ORDERS_LABEL);
    for (const [slot] of partition.storeSlotEntries) {
      keys.push(storeSlotExpandKey(slot));
    }
  }
  return keys;
}

/** Store tab in basket: expand time-slot groups only (no outer store header) */
export function defaultStoreSlotExpandKeys(
  partition: DeliveryOrderPartition<unknown>
): string[] {
  return partition.storeSlotEntries.map(([slot]) => storeSlotExpandKey(slot));
}

/** Partition delivery list items by subscription (daily) vs store */
export function partitionDeliveryItems<T extends { orderType?: string; deliveryTimeSlot?: string; deliverySlotInfo?: unknown }>(
  items: T[]
): DeliveryOrderPartition<T> {
  return partitionDailyAndStoreOrders(items, (item) => ({
    order_type: item.orderType,
    delivery_time_slot: item.deliveryTimeSlot,
    delivery_slot_info: item.deliverySlotInfo,
  }));
}

/** Slot label for DeliveryItem / grouping (empty for daily orders) */
export function getOrderTimeSlotRaw(
  order: Record<string, unknown> | null | undefined
): string {
  return getOrderDeliverySlotLabel(order);
}

/** Resolve slot label for order details modal */
export function resolveOrderSlotDisplay(order: OrderLikeForDetailsInput): string {
  const fromInfo = formatDeliverySlotLabel(
    parseSlotInfo(order.delivery_slot_info)
  );
  if (fromInfo) return fromInfo;
  const legacy = order.delivery_time_slot ?? order.delivery_time ?? "";
  return typeof legacy === "string" ? legacy.trim() : String(legacy ?? "").trim();
}

type OrderLikeForDetailsInput = {
  delivery_slot_info?: unknown;
  delivery_time_slot?: string;
  delivery_time?: string;
};
